import React, { useState, useRef, useEffect } from 'react'
import { Canvas } from '@react-three/fiber'
import { Character } from './Player'
import { Club } from './Club'
import nipplejs from 'nipplejs'
import { floorSize } from './utils/helpers'

// Joystick component that creates and manages the nipple.js instance
function Joystick({ onMove }) {
  const joystickRef = useRef(null);
  const nippleInstanceRef = useRef(null);

  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  useEffect(() => {
    // Only create joystick on touch devices
    if (isTouchDevice && joystickRef.current) {
      // Create nipple.js joystick
      const joystickWidth = 120;

      const options = {
        zone: joystickRef.current,
        mode: 'static',
        position: { left: 'calc(50% - 72px)', bottom: '93px' }, // Moved to left side
        color: 'white',
        size: joystickWidth
      };
      
      const nippleInstance = nipplejs.create(options);
      nippleInstanceRef.current = nippleInstance;
      
      // Add event listeners for joystick movement
      nippleInstance.on('move', (event, data) => {
        if (onMove && data.vector) {
          // Convert joystick data to directional values and normalize for consistent speed
          onMove({
            x: data.vector.x,
            y: -data.vector.y, // Invert Y for proper directional control
            force: data.force
          });
          
          // Check if event is a valid object before calling preventDefault
          if (event && typeof event.preventDefault === 'function') {
            event.preventDefault();
            event.stopPropagation();
          }
        }
      });
      
      nippleInstance.on('end', () => {
        // Reset when joystick is released - must reset to zeros
        if (onMove) {
          onMove({ x: 0, y: 0, force: 0 });
        }
      });
    }
    
    return () => {
      // Clean up nipple instance on unmount
      if (nippleInstanceRef.current) {
        nippleInstanceRef.current.destroy();
      }
    };
  }, [onMove]);
  
  if (isTouchDevice) {
    return (
      <div
        ref={joystickRef}
        style={{
          position: 'fixed',
          bottom: '0.5rem',
          left: 'calc(50% - 5rem)',  // Fixed position on left side
          width: '120px',
          height: '120px',
          zIndex: 1000,
          transform: 'none' // Remove transform to avoid positioning issues
        }}
      />
    );
  }
  
  return null;
}

export default function Game({ 
  setIsInFloor, 
  cornerIndexCurrent, 
  setCornerIndexCurrent, 
  cornerIndexNext, 
  setCornerIndexNext,
  flagToTriggerHandleHit,
  toggleAngleFlag,
  isFemale,
  useShaders,
  showTutorial,
  hideJoystick    
}) {
  const [joystickInput, setJoystickInput] = useState({ x: 0, y: 0, force: 0 });
  const [playerPosition, setPlayerPosition] = useState([0, 1, 0]);
  
  const restrictedRadius = 7;

  return (
    <>
      <Canvas camera={{ position: [0, 110, 0], fov: 100 }}>
        {/* Simple ambient light for base illumination */}
        <ambientLight intensity={0.5} />
        
        <directionalLight 
          position={[10, 20, 10]} 
          intensity={1}  
        />
        
        {/* Subtle hemisphere light for more natural lighting */}
        <hemisphereLight 
          args={['#ffffff', '#303030', 0.6]} 
        />
        
        {/* Pass joystick input to Character */}
        <Character 
          joystickInput={joystickInput} 
          setPlayerPosition={setPlayerPosition} 
          floorSize={floorSize}
          restrictedRadius={restrictedRadius}
          flagToTriggerHandleHit={flagToTriggerHandleHit}
          toggleAngleFlag={toggleAngleFlag}
          isFemale={isFemale}
        />
        
        <Club 
          playerPosition={playerPosition}
          setIsInFloor={setIsInFloor}
          cornerIndexCurrent={cornerIndexCurrent}
          setCornerIndexCurrent={setCornerIndexCurrent}
          cornerIndexNext={cornerIndexNext}
          setCornerIndexNext={setCornerIndexNext}
          restrictedRadius={restrictedRadius}
          useShaders={useShaders}
          showTutorial={showTutorial}
        />
      
      </Canvas>
      
      {/* Render joystick outside Canvas */}
      {!hideJoystick && <Joystick onMove={setJoystickInput} />}
    </>
  );
} 
